import { ChangedFieldsRow } from "./ChangedFieldsRow";
import { Column, InfoBox, RequisitionStatusBadge } from "@/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { shiftTypeToLabel } from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import { Job, Requisition, ShiftType } from "@/types";

import { JobSchema } from "../JobForm";

const formatShiftTypes = (types: ShiftType[]) =>
  types.map((t) => shiftTypeToLabel[t]).join(", ");

export const RequisitionsSection = ({
  affectedRequisitions,
  hasShiftTypeChanged,
  originalJob,
  formValues,
}: {
  affectedRequisitions: Requisition[];
  hasShiftTypeChanged: boolean;
  originalJob: Job;
  formValues: JobSchema;
}) => {
  const { userNameMap } = useTaggableUsers();

  const hasShiftDiffMinChanged =
    originalJob.shiftDifferentialMin !== formValues.shiftDifferentialMin;
  const hasShiftDiffMaxChanged =
    originalJob.shiftDifferentialMax !== formValues.shiftDifferentialMax;

  const changingFieldClassNames = "bg-amber-50 font-medium text-amber-800";

  return (
    <Column className="gap-3">
      <InfoBox variant="warning">
        The following requisitions that are in the live, pending, or backfill
        states will have their shift type and/or shift differential updated
        automatically.
      </InfoBox>
      <Column className="gap-1">
        {hasShiftTypeChanged && (
          <ChangedFieldsRow
            label="Shift Type"
            oldValue={formatShiftTypes(originalJob.shiftType)}
            newValue={formatShiftTypes(formValues.shiftType)}
          />
        )}
        {(hasShiftDiffMinChanged || hasShiftDiffMaxChanged) && (
          <>
            {hasShiftDiffMinChanged && (
              <ChangedFieldsRow
                label="Shift Diff Min"
                oldValue={formatting.currency(originalJob.shiftDifferentialMin)}
                newValue={formatting.currency(formValues.shiftDifferentialMin)}
              />
            )}
            {hasShiftDiffMaxChanged && (
              <ChangedFieldsRow
                label="Shift Diff Max"
                oldValue={formatting.currency(originalJob.shiftDifferentialMax)}
                newValue={formatting.currency(formValues.shiftDifferentialMax)}
              />
            )}
          </>
        )}
      </Column>
      <div className="rounded-md border border-slate-200">
        <Table className="w-full text-sm">
          <TableHeader>
            <TableRow>
              <TableHead>Req ID</TableHead>
              <TableHead>Assignees</TableHead>
              <TableHead>Status</TableHead>
              <TableHead
                className={cn(hasShiftTypeChanged && changingFieldClassNames)}
              >
                Shift Type
              </TableHead>
              <TableHead
                className={cn(
                  hasShiftDiffMinChanged && changingFieldClassNames,
                )}
              >
                Diff Min
              </TableHead>
              <TableHead
                className={cn(
                  hasShiftDiffMaxChanged && changingFieldClassNames,
                )}
              >
                Diff Max
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {affectedRequisitions.map((req) => (
              <TableRow key={req.id} className="border-t border-slate-200">
                <TableCell>
                  {req.externalReqId ?? `#${req.id.toString()}`}
                </TableCell>
                <TableCell>
                  {req.requisitionOwners.length > 0
                    ? req.requisitionOwners
                        .map((o) => userNameMap[o.userId])
                        .join(", ")
                    : "—"}
                </TableCell>
                <TableCell>
                  <RequisitionStatusBadge status={req.status} />
                </TableCell>
                <TableCell
                  className={cn(hasShiftTypeChanged && changingFieldClassNames)}
                >
                  {formatShiftTypes(req.shiftType)}
                </TableCell>
                <TableCell
                  className={cn(
                    hasShiftDiffMinChanged && changingFieldClassNames,
                  )}
                >
                  {formatting.currency(req.shiftDifferentialMin)}
                </TableCell>
                <TableCell
                  className={cn(
                    hasShiftDiffMaxChanged && changingFieldClassNames,
                  )}
                >
                  {formatting.currency(req.shiftDifferentialMax)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Column>
  );
};
